<template>
    <!-- Tasks other people sent you (routes/taskhandoffs.ts). Pops up until each one is done or declined;
         minimising shrinks it to a pill for this browser session, and a newly sent task opens it again. -->
    <template v-if="reminders.length">
        <div v-if="!minimised" class="task-reminders-backdrop">
            <div class="task-reminders" role="dialog" aria-modal="true" aria-labelledby="task-reminders-title" @keydown.esc="minimise()">
                <div class="task-reminders-head">
                    <h2 id="task-reminders-title">{{ reminders.length == 1 ? 'A task was sent to you' : reminders.length + ' tasks were sent to you' }}</h2>
                    <button type="button" class="task-reminders-min" ref="minButton" @click="minimise()">Minimise</button>
                </div>
                <div class="task-reminders-list">
                    <div v-for="r in reminders" :key="r.ID" class="task-reminder">
                        <div class="task-reminder-name">{{ r.Name }}</div>
                        <div class="task-reminder-meta">
                            From {{ r.SentByName || 'a teammate' }} · {{ r.MeetingName }} · on {{ r.BoardName }} ({{ r.Column }})
                        </div>
                        <div v-if="decliningID == r.ID" class="task-reminder-decline">
                            <textarea v-model="declineReason" maxlength="500" rows="2" placeholder="Why not? (optional - they'll see this)" aria-label="Reason for not doing this"></textarea>
                            <div class="task-reminder-btns">
                                <button type="button" class="btn" :disabled="busyID == r.ID" @click="decline(r)">Not doing this</button>
                                <button type="button" class="btn-invert" @click="decliningID = null">Back</button>
                            </div>
                        </div>
                        <div v-else class="task-reminder-btns">
                            <button type="button" class="btn" :disabled="busyID == r.ID" @click="done(r)">Done</button>
                            <button type="button" class="btn-invert" @click="open(r)">Open</button>
                            <button type="button" class="task-reminder-link" @click="startDecline(r)">Not doing this</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <button v-else type="button" class="task-reminders-pill" @click="expand()">
            {{ reminders.length }} task{{ reminders.length == 1 ? '' : 's' }} waiting for you
        </button>
    </template>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';

// sessionStorage: { minimised, seenID } - minimised for this browser session, until a task newer than seenID arrives
const STORAGE_KEY = 'taskReminders';

export default defineComponent({
    name: 'TaskReminders',
    data() {
        return {
            reminders: [] as any[],
            minimised: false,
            decliningID: null as any,
            declineReason: '',
            busyID: null as any,
            timer: null as any
        }
    },
    async created() {
        await this.load();
        // Picks up tasks sent while the app stays open
        this.timer = setInterval(() => { this.load(); }, 3 * 60 * 1000);
    },
    unmounted() {
        clearInterval(this.timer);
    },
    watch: {
        '$route.path'() {
            this.load();
        }
    },
    methods: {
        stored(): any {
            try {
                return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
            } catch(e) {
                return {};
            }
        },
        store(value: any) {
            try {
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
            } catch(e) { /* private mode: the pop-up just comes back on the next load */ }
        },
        newestID(): number {
            return Math.max(0, ...this.reminders.map((r: any) => Number(r.ID)));
        },
        async load() {
            await axios.get(`${this.$store.state.apiUrl}/api/taskhandoffs/mine`)
                .then(resp => {
                    this.reminders = resp.data;
                    const saved = this.stored();
                    this.minimised = !!saved.minimised && this.newestID() <= Number(saved.seenID || 0);
                    if(!this.minimised) {
                        this.$nextTick(() => { (this.$refs.minButton as any)?.focus(); });
                    }
                })
                .catch(e => {
                    console.log(e);
                })
        },
        minimise() {
            this.minimised = true;
            this.decliningID = null;
            this.store({ minimised: true, seenID: this.newestID() });
        },
        expand() {
            this.minimised = false;
            this.store({ minimised: false });
            this.$nextTick(() => { (this.$refs.minButton as any)?.focus(); });
        },
        open(r: any) {
            this.minimise();
            this.$router.push({ path: '/deck/' + r.Slug, query: { openCard: r.CardID } });
        },
        startDecline(r: any) {
            this.decliningID = r.ID;
            this.declineReason = '';
        },
        async done(r: any) {
            await this.respond(r, `${this.$store.state.apiUrl}/api/taskhandoffs/${r.ID}/done`, {});
        },
        async decline(r: any) {
            await this.respond(r, `${this.$store.state.apiUrl}/api/taskhandoffs/${r.ID}/decline`, { Reason: this.declineReason });
            this.decliningID = null;
        },
        async respond(r: any, url: string, body: any) {
            if(this.busyID) {
                return;
            }
            this.busyID = r.ID;
            await axios.post(url, body)
                .then(() => {
                    this.reminders = this.reminders.filter((existing: any) => existing.ID != r.ID);
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            this.busyID = null;
        }
    }
});
</script>

<style scoped>
.task-reminders-backdrop { position: fixed; inset: 0; z-index: 1000; background: rgba(20, 16, 12, .45); display: flex; align-items: center; justify-content: center; padding: 16px; }
.task-reminders { background: #fff; border-radius: 14px; width: 100%; max-width: 560px; max-height: calc(100vh - 32px); display: flex; flex-direction: column; box-shadow: 0 12px 40px rgba(0, 0, 0, .25); }
.task-reminders-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 18px 20px 12px; border-bottom: 1px solid var(--mid-gray); }
.task-reminders-head h2 { margin: 0; font-size: 18px; text-align: left; }
.task-reminders-min { border: 1px solid var(--mid-gray); background: #fff; border-radius: 8px; padding: 6px 12px; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.task-reminders-list { overflow-y: auto; padding: 6px 20px 18px; }
.task-reminder { border-bottom: 1px solid var(--light-gray); padding: 14px 0; }
.task-reminder:last-child { border-bottom: 0; }
.task-reminder-name { font-weight: 700; font-size: 15px; overflow-wrap: anywhere; }
.task-reminder-meta { font-size: 12.5px; color: var(--font-dark-gray); margin: 4px 0 10px; overflow-wrap: anywhere; }
.task-reminder-btns { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.task-reminder-btns .btn, .task-reminder-btns .btn-invert { margin: 0; }
.task-reminder-btns button:disabled { opacity: .5; cursor: default; }
.task-reminder-link { border: 0; background: none; color: #A33E29; font-size: 13px; text-decoration: underline; cursor: pointer; padding: 6px 0; }
.task-reminder-decline textarea { width: 100%; box-sizing: border-box; margin: 0 0 8px; }
.task-reminders-pill { position: fixed; z-index: 1000; bottom: 16px; left: 50%; transform: translateX(-50%); border: 0; border-radius: 22px; padding: 10px 18px; background: var(--primary-color); color: #fff; font-weight: 700; font-size: 13.5px; box-shadow: 0 6px 18px rgba(0, 0, 0, .25); cursor: pointer; white-space: nowrap; }
/* Clear of the bottom-right Search Processes button, which a centred pill meets on a phone */
@media(max-width: 700px) { .task-reminders-pill { bottom: 72px; } .task-reminders-head { padding: 16px 16px 10px; } .task-reminders-list { padding: 4px 16px 14px; } }
</style>
